import React from 'react';
import { MoreVertical, Settings, Trash2, RefreshCw, Plus } from 'lucide-react';
import { getStatusIcon, getProviderInfo } from '../../utils/providerUtils';
import Button from '../ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface Provider {
  id: string;
  name: string;
  type: string;
  status: string;
  configuration: Record<string, any>;
  lastSync?: string;
  isLoading?: boolean;
  isUnconfigured?: boolean;
  description?: string;
  credentialFields?: Array<{ name: string; label: string; type: string; required: boolean }>;
}

interface ProviderCardProps {
  provider: Provider;
  onEdit: (provider: Provider) => void;
  onDelete: (id: string) => void;
  onSync: (id: string) => void;
  onToggleStatus: (id: string) => void;
  isUnconfigured?: boolean;
}

const ProviderCard: React.FC<ProviderCardProps> = ({
  provider,
  onEdit,
  onDelete,
  onSync,
  onToggleStatus,
  isUnconfigured,
}) => {
  const providerInfo = getProviderInfo(provider.type);
  const statusIcon = getStatusIcon(provider.status);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border transition-all duration-200 ${
      isUnconfigured || provider.isUnconfigured
        ? 'border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50 hover:border-blue-300 dark:hover:border-blue-700'
        : 'border-gray-200 dark:border-gray-700 hover:shadow-md'
    }`}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            {providerInfo.logo}
            <div>
              <h3 className={`text-lg font-semibold ${
                isUnconfigured || provider.isUnconfigured
                  ? 'text-gray-600 dark:text-gray-400'
                  : 'text-gray-900 dark:text-white'
              }`}>
                {provider.name}
                {(isUnconfigured || provider.isUnconfigured) && (
                  <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                    (Not Configured)
                  </span>
                )}
              </h3>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`text-sm ${providerInfo.color}`}>
                  {providerInfo.name}
                </span>
                {!(isUnconfigured || provider.isUnconfigured) && (
                  <div className="flex items-center space-x-1">
                    {statusIcon}
                    <span className="text-sm text-gray-600 dark:text-gray-300 capitalize">
                      {provider.status}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {!(isUnconfigured || provider.isUnconfigured) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => onEdit(provider)}>
                  <Settings className="mr-2 h-4 w-4" />
                  Edit Configuration
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onSync(provider.id)}
                  disabled={provider.isLoading}
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${provider.isLoading ? 'animate-spin' : ''}`} />
                  Sync Now
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onDelete(provider.id)}
                  className="text-red-600 dark:text-red-400"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Provider
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Status & Sync Info */}
        <div className="space-y-2">
          {isUnconfigured || provider.isUnconfigured ? (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p className="mb-2">{provider.description}</p>
              {provider.credentialFields && (
                <div>
                  <span className="font-medium">Required credentials:</span>
                  <ul className="list-disc list-inside ml-2 text-xs mt-1">
                    {provider.credentialFields
                      .filter(field => field.required)
                      .map(field => (
                        <li key={field.name}>{field.label}</li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-300">Last Sync:</span>
                <span className="text-gray-900 dark:text-white">
                  {provider.lastSync ? new Date(provider.lastSync).toLocaleString() : 'Never'}
                </span>
              </div>
              
              {/* Configuration Preview */}
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <span>Zones: </span>
                <span className="text-gray-900 dark:text-white">
                  {(provider as any).zones ?? provider.configuration?.zones?.length ?? 0}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          {isUnconfigured || provider.isUnconfigured ? (
            <div className="w-full">
              <Button
                variant="default"
                size="sm"
                onClick={() => onEdit(provider)}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Configure Provider
              </Button>
            </div>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onToggleStatus(provider.id)}
                disabled={provider.isLoading}
              >
                {provider.status === 'active' ? 'Disable' : 'Enable'}
              </Button>
              
              <Button
                variant="default"
                size="sm"
                onClick={() => onEdit(provider)}
              >
                Configure
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderCard;